import { Chip, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { SalesOrderResponseEntity } from 'src/entities/SalesOrder.response.entity';

import { ConfirmDialog } from '../dialogs/SalesOrders/Confirm.dialog';

type Props = {
  status: string;
  entity: SalesOrderResponseEntity;
  updateEntity: (entity: SalesOrderResponseEntity) => void;
}

function StatusSelector({
  status,
  entity,
  updateEntity,
}: Props) {
  const statusMap = {
    NEW: { color: 'default' as const, label: '案件', variant: 'outlined' as const, customColor: {} },
    IN_PROGRESS: { color: 'info' as const, label: '調整中', variant: 'filled' as const, customColor: {} },
    AVAILABLE: { color: 'success' as const, label: '対応可能', variant: 'filled' as const, customColor: {} },
    CONFIRMED: { color: 'error' as const, label: '受注確定', variant: 'filled' as const, customColor: {} },
    FAILED: {
      color: 'warning' as const,
      label: '失注',
      variant: 'filled' as const,
      customColor: { backgroundColor: '#000', '&:hover': { backgroundColor: '#000' } }
    },
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [tmpStatus, setTmpStatus] = useState<keyof typeof statusMap>();

  const currentStatus = statusMap[(status || 'NEW') as keyof typeof statusMap];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (newStatus: keyof typeof statusMap) => {
    handleClose();
    if (newStatus === 'CONFIRMED') {
      setTmpStatus(newStatus);
      setOpenConfirmDialog(true);
      return;
    }
    update(newStatus, false);
  };

  const update = (newStatus: keyof typeof statusMap, applyToOrders: boolean) => {
    updateEntity({
      ...entity,
      status: newStatus,
      statusJa: statusMap[newStatus].label,
      applyToOrders,
    });
  };

  const handleApply = () => {
    update(tmpStatus, true);
    closeConfirmDialog();
  };

  const handleNotApply = () => {
    update(tmpStatus, false);
    closeConfirmDialog();
  };

  const handleConfirmCancel = () => {
    closeConfirmDialog();
  };

  const closeConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  return (
    <>
      <Chip
        size="small"
        color={currentStatus.color}
        label={currentStatus.label}
        variant={currentStatus.variant}
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          width: '100%',
          justifyContent: 'flex-start',
          ...currentStatus.customColor
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{ sx: { p: 0, m: 0, } }}
      >
        {Object.entries(statusMap).map(([sts, { label }]) => (
          <MenuItem
            key={sts}
            onClick={() => handleStatusChange(sts as keyof typeof statusMap)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
      <ConfirmDialog
        handleApply={handleApply}
        handleNotApply={handleNotApply}
        handleCancel={handleConfirmCancel}
        open={openConfirmDialog}
      />
    </>
  );
}

export default StatusSelector;
